import React, { useRef, useState, useEffect } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from '../../utils/styled'
import Container from './Container'
import { Link } from './Link'
import { ApplicationState } from '../../store'
import { Typography } from './Typography'
import { useUserLoggedInCheck } from '../../hooks/useUserLoggedInCheck'
import { useOutsideAlerter } from '../../hooks/useOutsideAlerter'
import { userLogout } from '../../store/user/actions'
import { UserIcon } from './UserIcon'
import { RouteType } from '../../types/routes'
import { ConditionalWrapper } from '../ConditionalWrapper/ConditionalWrapper'

interface PropsFromState {
  salonLogo?: string
  salonName?: string
}

type AllProps = PropsFromState

const Wrapper = styled('header')`
  padding: 0.5rem 1.5rem;
  background-color: ${props => props.theme.colors.brand};
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.headings};
`

const HeaderInner = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const HeaderLogoContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`

const HeaderLogo = styled('img')`
  width: 100%;
  max-width: 250px;
  height: 100px;
  max-height: 100px;
`

const UserIconContainer = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: none;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: 2.5rem;

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    display: flex;
  }
`

const HeaderUserContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
`

const DropdownWrapper = styled.div`
  position: relative;
`

const DropdownContainer = styled.div`
  display: none;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.brand};
  top: 100%;
  right: 0;
  padding: 0.5rem 2rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.borders};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    display: block;
  }
`

const HideOnMobile = styled.div`
  display: none;

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    display: block;
  }
`

const ROUTES_WITHOUT_HEADER_IN_MOBILE = [RouteType.Employees]

const Header: React.FC<AllProps> = ({ salonLogo, salonName }) => {
  const headerDropdownRef = useRef(null)
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState<boolean>(false)
  const [shouldAddConditionalWrapper, setShouldAddConditionalWrapper] = useState<boolean>(false)

  const location = useLocation()

  useEffect(() => {
    setShouldAddConditionalWrapper(ROUTES_WITHOUT_HEADER_IN_MOBILE.includes(location.pathname as RouteType))
  }, [location.pathname])

  useOutsideAlerter(headerDropdownRef, () => setIsHeaderDropdownOpen(false))

  const { isLoggedIn: isUserLoggedIn } = useSelector((state: ApplicationState) => state.user)

  const dispatch = useDispatch()

  useUserLoggedInCheck()

  const handleLogout = () => {
    setIsHeaderDropdownOpen(false)
    dispatch(userLogout())
  }

  const HideOnMobileWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return <HideOnMobile>{children}</HideOnMobile>
  }

  return (
    <>
      <ConditionalWrapper
        condition={shouldAddConditionalWrapper}
        wrapper={children => <HideOnMobileWrapper>{children}</HideOnMobileWrapper>}
      >
        <Wrapper>
          <Container>
            <HeaderInner>
              {salonName && salonLogo && (
                <Link to={RouteType.Home}>
                  <HeaderLogoContainer>
                    <HeaderLogo src={salonLogo} alt={salonName} />
                  </HeaderLogoContainer>
                </Link>
              )}
              {!salonName && !salonLogo ? (
                <Typography variant="h1">terminslot.de</Typography>
              ) : (
                <HeaderUserContainer>
                  <DropdownWrapper>
                    <UserIconContainer onClick={() => setIsHeaderDropdownOpen(!isHeaderDropdownOpen)}>
                      <UserIcon />
                    </UserIconContainer>

                    {isHeaderDropdownOpen && (
                      <DropdownContainer ref={headerDropdownRef}>
                        {isUserLoggedIn ? (
                          <>
                            <Link to={RouteType.Profile} onClick={() => setIsHeaderDropdownOpen(false)}>
                              <Typography variant="body1">Profil</Typography>
                            </Link>
                            <Link to={RouteType.UserBookings} onClick={() => setIsHeaderDropdownOpen(false)}>
                              <Typography variant="body1">Termine</Typography>
                            </Link>

                            <Link to={RouteType.Login} onClick={handleLogout}>
                              <Typography variant="body1">Logout</Typography>
                            </Link>
                          </>
                        ) : (
                          <Link to={RouteType.Login} onClick={() => setIsHeaderDropdownOpen(false)}>
                            <Typography variant="body1">Login</Typography>
                          </Link>
                        )}
                      </DropdownContainer>
                    )}
                  </DropdownWrapper>
                </HeaderUserContainer>
              )}
            </HeaderInner>
          </Container>
        </Wrapper>
      </ConditionalWrapper>
    </>
  )
}

const mapStateToProps = ({ salon }: ApplicationState) => ({
  salonLogo: salon.data?.logoPath,
  salonName: salon.data?.name
})

export default connect(mapStateToProps)(Header)
